/**
 * Sprievodca importu žije **nad stránkou**, nie na nej (KOLO 117).
 *
 * Dovtedy visel na `ZakazkyListPage`. Odchod na inú zákazku ho
 * odmountoval a s ním aj celý rozrobený import — takže „minimalizovať
 * a ísť sa niekam pozrieť" nebolo čo ponúknuť. Provider ho drží
 * namountovaný v `AppShell`, takže zavretie okna je naozaj len
 * schovanie: stav zostáva a plávajúci pruh vpravo dole vedie späť.
 *
 * Vzor je `lib/undo/UndoContext.tsx` — kontext s jednou akciou
 * a komponentom, ktorý si provider vykreslí sám.
 */
import { createContext, useContext, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  TopSolidMapovanieDialog,
  type CastCiel,
} from "@/components/import/TopSolidMapovanieDialog";
import {
  NovyImportZostavy,
  type VolbaImportu,
} from "@/components/import/NovyImportZostavy";
import type { Koncept } from "@/api/hooks/useTopsolidKoncept";
import type { ZakazkaNaPripojenie } from "@/components/zakazka/PripojitDialog";
import { usePrevzatieOdkazu } from "@/lib/import/usePrevzatieOdkazu";

interface ImportContextValue {
  /** Otvor sprievodcu (tlačidlo „Import z TopSolidu" na zozname). */
  otvorImport: () => void;
  /**
   * Odkaz z programu Tandem Zostavy (KOLO 127) — ukáže okno „Nový import
   * z Tandem Zostáv" pre **tento** koncept (KOLO 128), z ktorého sa
   * otvorí náhľad. Samostatná funkcia, nie voliteľný parameter
   * `otvorImport`: tá visí priamo na `onClick`, takže by do parametra
   * prišla udalosť kliknutia.
   */
  otvorKoncept: (konceptId: string) => void;
  /**
   * Otvor ho v režime **update** (KOLO 118) — opravený export do už
   * existujúceho výpočtu namiesto novej zákazky.
   */
  otvorUpdate: (zakazkaId: string, variantaId: string) => void;
}

const Ctx = createContext<ImportContextValue | null>(null);

export function ImportProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [update, setUpdate] = useState<{
    zakazkaId: string;
    variantaId: string;
  } | null>(null);
  /** Nová časť existujúcej zákazky (KOLO 203) — namiesto `update`. */
  const [cast, setCast] = useState<CastCiel | null>(null);
  /** Výroba k výpočtu (KOLO 208) — voľba okna nového importu. */
  const [kVypoctu, setKVypoctu] = useState<ZakazkaNaPripojenie | null>(null);
  const [koncept, setKoncept] = useState<string | null>(null);
  /** Koncept z odkazu, ktorý sa ponúka v okne „Nový import". */
  const [ponuka, setPonuka] = useState<string | null>(null);
  /** Koncept, na ktorom stojí sprievodca (otvorený aj zbalený). */
  const [aktivny, setAktivny] = useState<string | null>(null);

  /**
   * Odkaz na koncept. Opravený export z TopSolidu prepíše súbor **toho
   * istého** konceptu (KOLO 216) — keď je práve v sprievodcovi, okno
   * nového importu by ponúklo otvoriť to, čo je už otvorené. Sprievodca sa
   * len ukáže a súbor si načíta sám.
   */
  function ponukni(konceptId: string): void {
    if (konceptId === aktivny) {
      setKoncept(konceptId);
      setOpen(true);
      return;
    }
    setPonuka(konceptId);
  }

  /**
   * Obnov koncept v jeho režime. Aktualizácia potrebuje výpočet — keď ho
   * koncept ešte nemá (z Tandem Zostáv prichádza prázdny), vyberie sa
   * v okne; sem sa potom dostane cez `variantaId`.
   */
  function spusti(k: Koncept, volba?: VolbaImportu | null): void {
    setKVypoctu(volba?.typ === "novy" ? volba.kVypoctu : null);
    if (volba?.typ === "cast" && k.zakazka_id) {
      setCast({
        zakazkaId: k.zakazka_id,
        nazov: volba.nazov,
        doterajsiaNazov: volba.doterajsiaNazov,
      });
      setUpdate(null);
      setKoncept(k.id);
      return;
    }
    setCast(null);
    const v = (volba?.typ === "update" ? volba.variantaId : null) ?? k.varianta_id ?? null;
    if (k.rezim === "update" && k.zakazka_id && v) {
      setUpdate({ zakazkaId: k.zakazka_id, variantaId: v });
    } else {
      setUpdate(null);
    }
    setKoncept(k.id);
  }
  const navigate = useNavigate();
  // Odkaz, ktorým Chrome spustil appku alebo dal dopredu jej okno (KOLO 215)
  usePrevzatieOdkazu(ponukni);
  return (
    <Ctx.Provider
      value={{
        otvorImport: () => {
          setUpdate(null);
          setCast(null);
          setKVypoctu(null);
          setOpen(true);
        },
        otvorKoncept: ponukni,
        otvorUpdate: (zakazkaId, variantaId) => {
          setUpdate({ zakazkaId, variantaId });
          setCast(null);
          setOpen(true);
        },
      }}
    >
      {children}
      <NovyImportZostavy
        ponuka={ponuka}
        onPonukaHotova={() => setPonuka(null)}
        importOtvoreny={open}
        aktivnyKoncept={aktivny}
        onOtvor={spusti}
      />
      <TopSolidMapovanieDialog
        open={open}
        onOpenChange={setOpen}
        update={update}
        cast={cast}
        kVypoctuZOkna={kVypoctu}
        konceptNaOtvorenie={koncept}
        onKonceptOtvoreny={() => setKoncept(null)}
        onAktivnyKoncept={setAktivny}
        onVyberKoncept={(k) =>
          // Aktualizácia bez vybraného výpočtu sa musí najprv spýtať ktorého;
          // nová časť potrebuje názvy z okna (KOLO 203)
          k.rezim === "update" && (!k.varianta_id || k.cast_nova)
            ? setPonuka(k.id)
            : spusti(k)
        }
        onImported={(zakazkaId, variantaId) =>
          // Nová časť sa otvorí vo svojej záložke
          navigate(`/zakazky/${zakazkaId}${variantaId ? `?varianta=${variantaId}` : ""}`)
        }
      />
    </Ctx.Provider>
  );
}

export function useImport(): ImportContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useImport musí byť vnútri ImportProvider");
  return ctx;
}
